import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    TrackedRideCollectionContract,
    TrackedRideContract,
    VehicleType,
} from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { TrackedRide } from '@polaris-customerweb/sharedcomponents.user-content';
import { Feature } from 'geojson';
import { get } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserAuthService } from 'src/app/state/user-auth/user-auth.service';
import { GlobalService } from './global.service';

@Injectable({ providedIn: 'root' })
export class TrackedRideResourceService {
    private baseUrl: string;

    constructor(
        private configService: ConfigService,
        private globalService: GlobalService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService,
        private userAuthService: UserAuthService
    ) {
        this.baseUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'v2/rides'
        );
    }

    getTrackedRidesForUser(): Observable<TrackedRideCollectionContract> {
        return this.http.get(`${this.baseUrl}?includeDeleted=false`).pipe(
            map((rideDtos) => {
                return {
                    features: rideDtos,
                    type: 'FeatureCollection',
                } as TrackedRideCollectionContract;
            })
        );
    }

    updateTrackedRide(ride: TrackedRideContract | Feature): Observable<null> {
        return this.http.put<null>(this.baseUrl, ride);
    }

    getTrackedRideById(id: string): Observable<TrackedRide> {
        const url = this.routingHelpersService.combineUrl(this.baseUrl, id);
        return this.http.get<TrackedRideContract>(url).pipe(
            map((result) => {
                return new TrackedRide(result, this.configService.appSku);
            })
        );
    }

    deleteTrackedRide(id: string): Observable<void> {
        const url = this.routingHelpersService.combineUrl(this.baseUrl, id);
        return this.http.delete<undefined>(url);
    }

    convertTrackedRideToGpx(content: FormData): Observable<null> {
        const url = this.routingHelpersService.combineUrl(
            this.configService.apiUrl.rideCommand,
            'v2/ride/planned/convert'
        );
        return this.http.post<null>(url, content);
    }

    convertGpxToTrackedRide(
        content: FormData
    ): Observable<TrackedRideContract> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'convert-gpx'
        );
        return this.http.post(url, content) as Observable<TrackedRideContract>;
    }

    downloadGpx(id: string): void {
        this.userAuthService.getAuthToken$().subscribe((authToken) => {
            const url = this.routingHelpersService.combineUrl(
                this.configService.apiUrl.rideCommand,
                'v2/rides',
                id,
                `download?auth=${authToken}`
            );
            this.globalService.location.href = url;
        });
    }

    uploadGpx(content: FormData, rideType: VehicleType): Observable<string> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'upload'
        );
        const params = new HttpParams().set('rideType', rideType);
        return this.http
            .post<{ id: string } | null>(url, content, { params })
            .pipe(
                map((result: { id: string } | null) => get(result, 'id', ''))
            );
    }

    updateFlyByUrl(id: string): Observable<string> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            id,
            'flyby'
        );
        return this.http
            .post<{ flyByUrl: string }>(url, {})
            .pipe(map((result) => result.flyByUrl));
    }
}
